import axios from 'axios';
import calculateStatus from '../hooks/useComputeStatus';
import { TOKEN_EXPIRED, TIMEOUT } from '../hooks/useAPIHelper';
import { reactiveRowToJob, applyPortalStatus } from './reactiveCallPagesShape';

/**
 * The newest few reactive calls a person may see, for Home's "Recent Reactive Maintenance" list (PORTAL-199), from
 * the database in one small query. Home used to read the customer's whole paged set to find four rows; a paged set
 * is rebuilt in full every ten minutes it is in use. THERE IS NO FALLBACK TO THE LIVE PRONTO CALL.
 *
 * THREE ANSWERS, which the caller has to tell apart:
 *   - a list            the newest calls the person may see (possibly empty: their contracts hold no calls).
 *   - null              the database found NO ENTITLEMENT for the person. That is not "no calls", so it is not
 *                       shown as an empty list; the caller takes its list from the paged set instead, filtered by
 *                       the contracts as it always was.
 *   - throws TIMEOUT    it could not answer. Home already shows that as its "could not load" state. An expired
 *                       Pronto token throws the expired-token words, exactly as the live call does.
 *
 * Each call has the fields of a page row, so it is mapped by the same function and its status is the database's own
 * (applyPortalStatus), as for the paged list.
 */

const RECENT_URL = '/.netlify/functions/xServiceGetReactiveCallRecent';

const GetReactiveCallRecent = async (token: string, userName: string, customerId: string): Promise<any[] | null> => {
  const started = Date.now();

  try {
    const reply = await axios.post(
      RECENT_URL,
      `<Filters><CustomerCode><Like>${customerId}</Like></CustomerCode></Filters>`,
      { headers: { 'Content-Type': 'application/xml', 'X-Pronto-Token': token, 'X-Pronto-Username': userName } },
    );

    const data = reply.data;

    if (!Array.isArray(data?.calls)) {
      throw new Error('the answer has no list of calls');
    }

    if (!(Number(data?.entitlementRows) > 0)) {
      console.log('Recent reactive calls: no entitlement found for this person - using the paged list');
      return null;
    }

    const jobs = calculateStatus(data.calls.map(reactiveRowToJob) as any).map(applyPortalStatus);

    console.log(`Recent reactive calls: ${jobs.length} rows in ${Date.now() - started} ms`);

    return jobs;
  } catch (err: any) {
    if (err?.response?.status === 401 && err.response.data === 'TokenExpired') {
      throw TOKEN_EXPIRED;
    }

    console.log('Recent reactive calls unavailable - not calling Pronto live:', err?.message ?? err);
    throw TIMEOUT;
  }
};

export default GetReactiveCallRecent;
